import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Users, HeartHandshake, ArrowRight, Search, SlidersHorizontal, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { useLanguageOptions, useLanguageLabeler } from "../lib/taxonomy";
import { EmptyState } from "../components/EmptyState";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";

const ANY = "any";
const emptyFilters = { q: "", location: "", language: ANY, online: false, in_person: false, status: ANY, open_only: false, date_from: "", date_to: "" };
export function InitiativeCard({ i }) {
  const { t, lang } = useLang();
  const goal = i.participation_goal || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((i.confirmed / goal) * 100)) : 0;
  const joinedTxt = `${i.confirmed} ${i.confirmed === 1 ? t("initiatives.joined_singular") : t("initiatives.joined_plural")}`;
  const remaining = i.remaining ?? 0;
  const spotsTxt = (remaining === 1 ? t("initiatives.spots_remaining_one") : t("initiatives.spots_remaining_other")).replace("{n}", remaining);
  return (
    <Link to={`/iniciativas/${i.id}`} data-testid={`initiative-card-${i.id}`}
      className="group flex flex-col rounded-2xl border border-border bg-white p-7 transition-all hover:-translate-y-0.5 hover:border-terracotta/40 hover:shadow-[0_8px_30px_rgba(200,109,81,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-xl font-semibold tracking-tight text-slateink">{i.name}</h3>
        <StatusBadge status={i.status} label={t(`status.${i.status}`)} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {i.organizer_name && <span>{t("initiatives.organizer")}: {i.organizer_name}</span>}
        {i.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{i.location}</span>}
        {i.dates && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{i.dates}</span>}
      </div>
      {i.description && <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-slateink/70">{i.description}</p>}

      <div className="mt-6">
        {i.status === "closed" ? (
          <span data-testid="card-momentum" className="inline-flex items-center gap-1.5 text-xs font-medium text-slateink/60">
            <Users className="h-3.5 w-3.5" />{joinedTxt} · {t("status.closed")}
          </span>
        ) : i.is_full ? (
          <span data-testid="card-momentum" className="inline-flex items-center gap-1.5 text-xs font-medium text-slateink/70">
            <Users className="h-3.5 w-3.5 text-terracotta" />{joinedTxt} · {t("initiatives.full_short")}
          </span>
        ) : (
          <span data-testid="card-momentum" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5 text-terracotta" />{joinedTxt}{goal > 0 ? ` · ${spotsTxt}` : ""}
          </span>
        )}
        {goal > 0 && !i.is_full && i.status !== "closed" && (
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-terracotta transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-terracotta">{t("common.view")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
    </Link>
  );
}

export default function Initiatives() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(emptyFilters);
  const [sheetOpen, setSheetOpen] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (f.q) params.q = f.q;
    if (f.location) params.location = f.location;
    if (f.language !== ANY) params.language = f.language;
    if (f.online) params.online = true;
    if (f.in_person) params.in_person = true;
    if (f.status !== ANY) params.status = f.status;
    if (f.open_only) params.open_only = true;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    api.get("/initiatives", { params }).then((r) => setList(r.data)).catch(() => setList([])).finally(() => setLoading(false));
  }, [f]);

  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);

  const hasFilters = f.q || f.location || f.language !== ANY || f.online || f.in_person || f.status !== ANY || f.open_only || f.date_from || f.date_to;
  const reset = () => setF(emptyFilters);
  const langOptions = useLanguageOptions();
  const langLabel = useLanguageLabeler();

  const FiltersPanel = () => (
    <div className="space-y-5" data-testid="initiatives-filters">
      <div>
        <Label>{t("initiatives.f_location")}</Label>
        <Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="filter-location" />
      </div>
      <div>
        <Label>{t("initiatives.f_language")}</Label>
        <Select value={f.language} onValueChange={(v) => set("language", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-language"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("common.all")}</SelectItem>
            {langOptions.map((l) => <SelectItem key={l.value} value={l.value}>{langLabel(l.value, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{t("initiatives.f_status")}</Label>
        <Select value={f.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("initiatives.all_statuses")}</SelectItem>
            <SelectItem value="published">{t("status.published")}</SelectItem>
            <SelectItem value="closed">{t("status.closed")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("initiatives.f_date_from")}</Label>
          <Input type="date" value={f.date_from || ""} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" />
        </div>
        <div>
          <Label>{t("initiatives.f_date_to")}</Label>
          <Input type="date" value={f.date_to || ""} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" />
        </div>
      </div>
      <div className="space-y-3 border-t border-border pt-4">
        {[["online", t("common.online")], ["in_person", t("common.in_person")], ["open_only", t("initiatives.f_open")]].map(([k, label]) => (
          <label key={k} className="flex cursor-pointer items-center gap-2.5 text-sm text-slateink/80">
            <Checkbox checked={f[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`filter-${k}`} />{label}
          </label>
        ))}
      </div>
      {hasFilters && (
        <Button variant="outline" className="w-full rounded-full" onClick={reset} data-testid="clear-filters-btn">
          <X className="mr-1.5 h-4 w-4" />{t("initiatives.clear_filters")}
        </Button>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 text-terracotta"><HeartHandshake className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">{t("initiatives.title")}</span></div>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slateink sm:text-4xl">{t("initiatives.title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("initiatives.subtitle")}</p>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("initiatives.search_placeholder")} value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="initiatives-search" />
        </div>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="rounded-full lg:hidden" data-testid="open-filters-btn">
              <SlidersHorizontal className="mr-1.5 h-4 w-4" />{t("initiatives.filters")}
              {hasFilters && <span className="ml-1.5 h-2 w-2 rounded-full bg-terracotta" />}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto" data-testid="filters-sheet">
            <SheetHeader><SheetTitle>{t("initiatives.filters")}</SheetTitle></SheetHeader>
            <div className="mt-6">{FiltersPanel()}</div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="mt-8 lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-border bg-white p-6">
            <p className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground"><SlidersHorizontal className="h-4 w-4" />{t("initiatives.filters")}</p>
            {FiltersPanel()}
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid gap-6 md:grid-cols-2">{[0, 1].map((k) => <div key={k} className="h-56 animate-pulse rounded-2xl bg-white/60" />)}</div>
          ) : list.length === 0 ? (
            hasFilters
              ? <EmptyState icon={Search} title={t("initiatives.no_match_title")} description={t("initiatives.no_match_desc")}
                  action={<Button variant="outline" className="rounded-full" onClick={reset} data-testid="empty-clear-filters">{t("initiatives.clear_filters")}</Button>} testId="initiatives-no-match" />
              : <EmptyState icon={HeartHandshake} title={t("initiatives.empty_title")} description={t("initiatives.empty_desc")} testId="initiatives-empty" />
          ) : (
            <div className="grid gap-6 md:grid-cols-2">{list.map((i) => <InitiativeCard key={i.id} i={i} />)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
